import type { IFilterableListItem, IOmit, ISuggestProps } from '@sixui/ui';
import { forwardRef } from 'react';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, Skeleton, Suggest } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '~/components/atoms/UserAvatar';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useOrganizationsQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationsQuery';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';

export type IOrganizationSelectorProps = IOmit<ISuggestProps, 'items'> & {
  allowPersonalAccount?: boolean;
  canCreate?: boolean;
  createUrl?: string;
};

const styles = stylex.create({
  avatar: {
    width: 32,
    height: 32,
  },
});

export const OrganizationSelector = forwardRef<
  HTMLInputElement,
  IOrganizationSelectorProps
>(function OrganizationSelector(props, forwardedRef) {
  const { allowPersonalAccount, canCreate, createUrl, ...other } = props;
  const { t } = useTranslation();
  const organizationQuery = useOrganizationsQuery();
  const userQuery = useUserQuery({});
  const userInfo = userQuery.data ? parseUserInfo(userQuery.data) : undefined;
  const loaded =
    (!!organizationQuery.data && !!userQuery.data) ||
    (!organizationQuery.isLoading && !userQuery.isLoading);
  const items: Array<IFilterableListItem> = [
    ...(allowPersonalAccount && userInfo
      ? [
          {
            value: '',
            label: userInfo.fullName,
            leading: <UserAvatar user={userQuery.data} sx={styles.avatar} />,
            supportingText: t('OrganizationSelector.personal-account'),
          },
        ]
      : []),
    ...(organizationQuery.data?.items.map(({ _id, name }) => ({
      value: _id,
      label: name,
      icon: <FontAwesomeIcon icon={faBuilding} />,
    })) ?? []),
    ...(canCreate
      ? [
          {
            value: '__create',
            label: t('OrganizationSelector.create'),
            icon: <FontAwesomeIcon icon={faPlus} />,
            href: createUrl ?? '/organizations/create',
          },
        ]
      : []),
  ];

  return organizationQuery.isError ? (
    <ErrorMessage error={organizationQuery.error} />
  ) : loaded ? (
    <>
      <Suggest {...other} items={items} ref={forwardedRef} />
    </>
  ) : (
    <Skeleton loaded={loaded}>
      <Field />
    </Skeleton>
  );
});
